import React, { Fragment } from 'react';

const THUMB_SIZE = 56;

function formatBytes(bytes) {
  if (!bytes) return '–';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(isoStr) {
  if (!isoStr) return '–';
  const d = new Date(isoStr);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * Props:
 *   item          – gallery item object
 *   isSelected    – whether the row is selected
 *   onToggleSelect – () => void to toggle selection
 *   onDelete        – () => void to delete the item
 *   disabled      – disable interaction
 */
export default function GalleryItemRow({ item, isSelected, onToggleSelect, onDelete, disabled = false }) {
  const isVideo = item.media_type === 'video';
  const src = item.media_url;
  const thumbSrc = item.thumbnail_url;

  return (
    <Fragment key={item.id}>
      <tr className="gallery-item-row">
        {/* Thumbnail */}
        <td style={{ width: THUMB_SIZE + 16, padding: '0.4rem 0.5rem' }}>
          <div
            style={{
              width: THUMB_SIZE,
              height: THUMB_SIZE,
              borderRadius: '0.25rem',
              overflow: 'hidden',
              background: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              position: 'relative',
            }}
          >
            {isVideo ? (
              <>
                {thumbSrc ? (
                  <img
                    src={thumbSrc}
                    alt={item.item_name}
                    loading="lazy"
                    decoding="async"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <span aria-label="Video" style={{ fontSize: '1.5rem' }}>🎬</span>
                )}
                <span
                  aria-label="Play video"
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: '1rem',
                    color: 'white',
                    textShadow: '0 0 4px rgba(0,0,0,0.6)',
                    pointerEvents: 'none',
                  }}
                >
                  ▶
                </span>
              </>
            ) : (
              <img
                src={src}
                alt={item.item_name}
                loading="lazy"
                decoding="async"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
          </div>
        </td>

        {/* Name */}
        <td>
          <span style={{ fontWeight: 500, wordBreak: 'break-word' }}>{item.item_name}</span>
        </td>

        {/* Event */}
        <td className="hide-mobile">
          {item.event_id ? (
            <span className="badge badge-event">{item.event_name}</span>
          ) : (
            <span style={{ color: '#888', fontStyle: 'italic' }}>Ad-hoc</span>
          )}
        </td>

        {/* Year */}
        <td className="hide-mobile" style={{ whiteSpace: 'nowrap' }}>
          {item.year}
        </td>

        {/* Media type */}
        <td>
          <span className={`badge badge-${isVideo ? 'video' : 'photo'}`}>
            {isVideo ? 'Video' : 'Photo'}
          </span>
        </td>

        {/* Size */}
        <td className="hide-mobile" style={{ whiteSpace: 'nowrap', color: '#666', fontSize: '0.85em' }}>
          {formatBytes(item.size_bytes)}
        </td>

        {/* Uploaded */}
        <td className="hide-mobile" style={{ whiteSpace: 'nowrap', color: '#666', fontSize: '0.85em' }}>
          {formatDate(item.uploaded_at || item.date)}
        </td>

        {/* Select checkbox */}
        <td style={{ textAlign: 'center', padding: '0.25rem 0.5rem' }}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => onToggleSelect(item.id)}
            disabled={disabled}
            style={{ accentColor: '#0d6efd', cursor: 'pointer' }}
          />
        </td>

        {/* Actions - delete button */}
        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={() => onDelete?.(item.id)}
            disabled={disabled}
            aria-label={`Delete ${item.item_name}`}
          >
            🗑
          </button>
        </td>
      </tr>
    </Fragment>
  );
}
